import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { useAuth } from "@clerk/react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { isAuthDisabled } from "@/lib/authMode";
import {
  Sparkles,
  Mail,
  Bot,
  FolderKanban,
  CheckCircle2,
  ArrowRight,
  Clock,
  Lock,
  Mic,
  FileText,
  Calendar,
  Wifi,
  Check,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import LegalFooter from "@/components/legal-footer";
import LanguageSwitcher from "@/components/language-switcher";
import Logo from "@/components/logo";
import {
  PUBLIC_PLANS,
  PUBLIC_FAQ,
  priceHtFromTtc,
  formatEur,
} from "@/lib/pricing-data";

/**
 * Public marketing landing page for OneNext. Rendered for signed-out
 * visitors at "/". Signed-in users are bounced to /app/today by
 * HomeRedirect in App.tsx.
 *
 * Design notes:
 * - Stays dependency-light: framer-motion (already in the bundle for
 *   other animations) + lucide icons + the existing shadcn Button.
 * - Uses the global --primary token (just bumped to a more vibrant
 *   green per user request) so the marketing page stays visually
 *   coherent with the app shell.
 * - All copy in French to match the rest of the product.
 */
export default function LandingPage() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground antialiased">
      <NavBar />
      <Hero />
      <LogosBand />
      <FeaturesGrid />
      <HowItWorks />
      <PricingSection />
      <Testimonial />
      <FaqSection />
      <FinalCTA />
      <LegalFooter />
    </div>
  );
}

// ─── Nav ───────────────────────────────────────────────────────────────

function NavBar() {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const bg = useTransform(
    scrollY,
    [0, 80],
    ["hsl(var(--background) / 0)", "hsl(var(--background) / 0.85)"],
  );
  const border = useTransform(
    scrollY,
    [0, 80],
    ["hsl(var(--border) / 0)", "hsl(var(--border) / 1)"],
  );

  // useAuth() doit toujours être appelé (règle des hooks).
  // En mode AUTH_DISABLED, on ignore le résultat Clerk et on considère
  // l'utilisateur comme connecté (le serveur redirige vers le dashboard).
  const { isSignedIn: clerkSignedIn } = useAuth();
  const loggedIn = isAuthDisabled() || !!clerkSignedIn;

  return (
    <motion.header
      style={{ backgroundColor: bg, borderBottomColor: border }}
      className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-md"
    >
      <div className="mx-auto max-w-6xl flex items-center justify-between px-6 h-16">
        <Link href="/" className="flex items-center gap-2 group">
          <Logo size={32} />
          <span className="font-semibold tracking-tight text-lg">
            OneNext
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <a href="#features" className="hover:text-foreground transition-colors">
            {t("nav.features")}
          </a>
          <a href="#how" className="hover:text-foreground transition-colors">
            {t("nav.how_it_works")}
          </a>
          <a href="#pricing" className="hover:text-foreground transition-colors">
            {t("nav.pricing")}
          </a>
          <a href="#faq" className="hover:text-foreground transition-colors">
            {t("nav.faq")}
          </a>
          <a href="#testimonial" className="hover:text-foreground transition-colors">
            {t("nav.testimonials")}
          </a>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {loggedIn ? (
            <Link href="/app/today">
              <Button size="sm" className="gap-1.5">
                {t("nav.dashboard")}
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          ) : (
            <Link href="/sign-in">
              <Button size="sm" className="gap-1.5">
                {t("nav.login")}
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </motion.header>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation();
  const { isSignedIn: clerkSignedIn } = useAuth();
  const loggedIn = isAuthDisabled() || !!clerkSignedIn;

  return (
    <section className="relative overflow-hidden pt-32 pb-24 px-6">
      {/* Animated gradient blobs */}
      <BackgroundBlobs />

      <div className="relative mx-auto max-w-6xl grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]"
          >
            {t("hero.title_1")}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-primary">{t("hero.title_2")}</span>
              <motion.span
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
                style={{ originX: 0 }}
                className="absolute left-0 bottom-1 h-3 w-full rounded-md bg-primary/20"
              />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link href="/sign-up">
              <Button size="lg" className="gap-2 h-12 px-6 text-base shadow-lg shadow-primary/20">
                {t("hero.cta_start")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link href="/demo">
              <Button
                variant="outline"
                size="lg"
                className="h-12 px-6 text-base gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {t("hero.cta_demo")}
              </Button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex items-center gap-6 text-xs text-muted-foreground"
          >
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              {t("hero.trust_no_card")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              {t("hero.trust_hosted")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              {t("hero.trust_encrypted")}
            </span>
          </motion.div>
        </div>

        <HeroVisual />
      </div>
    </section>
  );
}

function BackgroundBlobs() {
  return (
    <div aria-hidden className="absolute inset-0 -z-10 overflow-hidden">
      <motion.div
        animate={{
          x: [0, 40, 0],
          y: [0, -30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-primary/20 blur-3xl"
      />
      <motion.div
        animate={{
          x: [0, -30, 0],
          y: [0, 40, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-20 -right-32 w-[520px] h-[520px] rounded-full bg-accent/10 blur-3xl"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,hsl(var(--primary)/0.05),transparent_60%)]" />
    </div>
  );
}

/**
 * Animated mock of a "Today" task list — gives the hero a tangible
 * preview of what the product looks like, without shipping screenshots.
 */
function HeroVisual() {
  const { t } = useTranslation();
  const tasks = [
    { title: t("landing_anim.hero_task1"), time: "10 min", done: true },
    { title: t("landing_anim.hero_task2"), time: "45 min", done: false },
    { title: t("landing_anim.hero_task3"), time: "20 min", done: false },
    { title: t("landing_anim.hero_task4"), time: "30 min", done: false },
  ];
  const [checked, setChecked] = useState<number[]>([0]);

  // Auto-tick the next item every 2.5s for a subtle "look, things get
  // done" loop. Resets when all are done.
  useEffect(() => {
    const id = setInterval(() => {
      setChecked((prev) => {
        if (prev.length >= tasks.length) return [0];
        return [...prev, prev.length];
      });
    }, 2500);
    return () => clearInterval(id);
  }, [tasks.length]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
      className="relative"
    >
      {/* Halo */}
      <div className="absolute -inset-6 bg-primary/20 blur-3xl rounded-[2rem] -z-10" />

      <div className="relative rounded-2xl border bg-card shadow-2xl overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b bg-muted/30">
          <span className="w-2.5 h-2.5 rounded-full bg-destructive/40" />
          <span className="w-2.5 h-2.5 rounded-full bg-accent/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-primary/60" />
          <span className="ml-3 text-xs text-muted-foreground">
            {t("hero.hero_today")}
          </span>
        </div>
        <div className="p-5 space-y-2">
          {tasks.map((task, i) => {
            const isDone = checked.includes(i);
            return (
              <motion.div
                key={task.title}
                layout
                className={`flex items-center gap-3 rounded-lg border px-3 py-3 transition-colors ${
                  isDone
                    ? "bg-primary/5 border-primary/20"
                    : "bg-background border-border"
                }`}
              >
                <motion.div
                  animate={{
                    backgroundColor: isDone
                      ? "hsl(var(--primary))"
                      : "hsl(var(--background))",
                    borderColor: isDone
                      ? "hsl(var(--primary))"
                      : "hsl(var(--border))",
                  }}
                  className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0"
                >
                  {isDone && (
                    <motion.svg
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="w-3 h-3 text-primary-foreground"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <motion.path d="M5 12l5 5L20 7" />
                    </motion.svg>
                  )}
                </motion.div>
                <span
                  className={`flex-1 text-sm font-medium ${
                    isDone
                      ? "line-through text-muted-foreground"
                      : "text-foreground"
                  }`}
                >
                  {task.title}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {task.time}
                </span>
              </motion.div>
            );
          })}
        </div>
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{t("hero.hero_progress")}</span>
            <span className="tabular-nums">
              {checked.length} / {tasks.length}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div
              animate={{
                width: `${(checked.length / tasks.length) * 100}%`,
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="h-full bg-primary"
            />
          </div>
        </div>
      </div>

      {/* Floating "AI suggestion" card */}
      <motion.div
        initial={{ opacity: 0, x: 30, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.6, delay: 0.9 }}
        className="absolute -right-4 -bottom-6 w-64 rounded-xl border bg-card shadow-xl p-3"
      >
        <div className="flex items-start gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold">{t("hero.ai_suggestion_label")}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
              {t("hero.ai_suggestion_text")}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Logos band (social proof placeholder) ─────────────────────────────

function LogosBand() {
  const { t } = useTranslation();
  const items = [
    t("logos.freelancers"),
    t("logos.sme"),
    t("logos.creative"),
    t("logos.consultants"),
    t("logos.product_teams"),
  ];
  return (
    <section className="border-y bg-muted/20 py-8 px-6">
      <div className="mx-auto max-w-6xl">
        <p className="text-center text-xs uppercase tracking-widest text-muted-foreground mb-4">
          {t("logos.built_for")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm font-medium text-muted-foreground/80">
          {items.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Features grid ─────────────────────────────────────────────────────
//
// Each feature card has its own bespoke mini-animation (rendered above the
// title/description). Visuals are intentionally compact (~160px tall) so
// the grid stays scannable, and they only start animating once the card
// scrolls into view to keep the page light at first paint.

interface Feature {
  title: string;
  description: string;
  Visual: () => ReactNode;
}

function FeaturesGrid() {
  const { t } = useTranslation();

  const FEATURES: Feature[] = [
    {
      title: t("features.f1_title"),
      description: t("features.f1_desc"),
      Visual: FocusVisual,
    },
    {
      title: t("features.f2_title"),
      description: t("features.f2_desc"),
      Visual: MailTriageVisual,
    },
    {
      title: t("features.f3_title"),
      description: t("features.f3_desc"),
      Visual: CaptureVisual,
    },
    {
      title: t("features.f4_title"),
      description: t("features.f4_desc"),
      Visual: ProjectsVisual,
    },
    {
      title: t("features.f5_title"),
      description: t("features.f5_desc"),
      Visual: AssistantVisual,
    },
    {
      title: t("features.f6_title"),
      description: t("features.f6_desc"),
      Visual: SecurityVisual,
    },
  ];

  return (
    <section id="features" className="py-24 px-6">
      <div className="mx-auto max-w-6xl">
        <SectionHeader
          eyebrow={t("features.eyebrow")}
          title={t("features.title")}
          subtitle={t("features.subtitle")}
        />

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }: { feature: Feature; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { Visual } = feature;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.06, ease: "easeOut" }}
      className="group relative rounded-2xl bg-card shadow-sm hover:shadow-md overflow-hidden transition-all flex flex-col"
    >
      {/* Visual zone — fixed height, gradient background, clipped */}
      <div className="relative h-44 overflow-hidden bg-gradient-to-br from-primary/5 via-background to-primary/10">
        {/* Only mount the animation when in view so off-screen cards stay cheap */}
        {inView && <Visual />}
      </div>
      <div className="p-6 flex-1">
        <h3 className="text-lg font-semibold tracking-tight">{feature.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          {feature.description}
        </p>
      </div>
    </motion.div>
  );
}

// ─── Per-feature animated visuals ──────────────────────────────────────

/** Aujourd'hui : 5 tasks, 4 dim out, the focus task glows. */
function FocusVisual() {
  const { t } = useTranslation();
  const tasks = [t("landing_anim.focus_task1"), t("landing_anim.focus_task2"), t("landing_anim.focus_task3"), t("landing_anim.focus_task4"), t("landing_anim.focus_task5")];
  const focusIdx = 2;
  return (
    <div className="absolute inset-0 p-4 flex flex-col justify-center gap-1.5">
      {tasks.map((t, i) => {
        const isFocus = i === focusIdx;
        return (
          <motion.div
            key={t}
            initial={{ opacity: 0, x: -10 }}
            animate={{
              opacity: isFocus ? 1 : 0.25,
              x: 0,
              filter: isFocus ? "blur(0px)" : "blur(0.5px)",
            }}
            transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
            className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs ${
              isFocus
                ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                : "bg-muted/40 text-foreground"
            }`}
          >
            <div
              className={`w-3 h-3 rounded-sm border ${
                isFocus
                  ? "bg-primary-foreground/20 border-primary-foreground/60"
                  : "border-foreground/30"
              }`}
            />
            <span className="truncate">{t}</span>
            {isFocus && (
              <motion.span
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.9, type: "spring" }}
                className="ml-auto text-[10px] font-semibold uppercase tracking-wide bg-primary-foreground/20 px-1.5 py-0.5 rounded"
              >
                Focus
              </motion.span>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

/** Mails triés : 3 envelopes fly in, then split into 3 buckets. */
function MailTriageVisual() {
  const { t } = useTranslation();
  const buckets = [
    { label: t("landing_anim.mail_label_action"), color: "text-primary bg-primary/15", count: 2 },
    { label: t("landing_anim.mail_label_fyi"), color: "text-amber-600 bg-amber-500/15", count: 4 },
    { label: t("landing_anim.mail_label_noise"), color: "text-muted-foreground bg-muted", count: 12 },
  ];
  // Cycle length must mirror the envelope animation: 2.4s flight +
  // 1.2s repeatDelay = 3.6s. We ramp counters from 0 → target over
  // ~1.6s (envelope landing window), hold, then reset to align with
  // the next envelope wave.
  const CYCLE_MS = 3600;
  const RAMP_MS = 1600;
  const RAMP_FRAMES = 24;
  const [progress, setProgress] = useState(0); // 0..1, ramp position

  useEffect(() => {
    // Track the current ramp + cycle timers so unmount stops both,
    // not just whatever was scheduled on the first invocation.
    let rampInterval: ReturnType<typeof setInterval> | null = null;
    let cycleTimer: ReturnType<typeof setTimeout> | null = null;
    const tickStep = RAMP_MS / RAMP_FRAMES;

    const runRamp = () => {
      let frame = 0;
      setProgress(0);
      if (rampInterval) clearInterval(rampInterval);
      rampInterval = setInterval(() => {
        frame += 1;
        setProgress(Math.min(1, frame / RAMP_FRAMES));
        if (frame >= RAMP_FRAMES && rampInterval) {
          clearInterval(rampInterval);
          rampInterval = null;
        }
      }, tickStep);
      cycleTimer = setTimeout(runRamp, CYCLE_MS);
    };

    runRamp();
    return () => {
      if (rampInterval) clearInterval(rampInterval);
      if (cycleTimer) clearTimeout(cycleTimer);
    };
  }, []);

  return (
    <div className="absolute inset-0 p-4 flex flex-col">
      {/* Flying envelopes from top */}
      <div className="relative h-12">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            initial={{ x: -20, y: -30, opacity: 0, rotate: -10 }}
            animate={{
              x: [0, 30 + i * 28, 30 + i * 28],
              y: [-30, 10, 60],
              opacity: [0, 1, 0],
              rotate: [-10, 0, 5],
            }}
            transition={{
              duration: 2.4,
              delay: i * 0.35,
              repeat: Infinity,
              repeatDelay: 1.2,
              ease: "easeInOut",
            }}
            className="absolute top-0 left-0"
          >
            <Mail className="w-4 h-4 text-primary/70" />
          </motion.div>
        ))}
      </div>
      {/* Buckets at bottom */}
      <div className="mt-auto grid grid-cols-3 gap-2">
        {buckets.map((b, i) => {
          // Stagger each bucket's count-up slightly so they don't all
          // tick in lockstep — feels more like sorting in real time.
          const offset = i * 0.15;
          const local = Math.max(0, Math.min(1, (progress - offset) / (1 - offset)));
          // easeOut so the counter decelerates near the target value.
          const eased = 1 - Math.pow(1 - local, 2);
          const value = Math.round(b.count * eased);
          return (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
              className={`rounded-lg px-2 py-2 text-center ${b.color}`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wide">
                {b.label}
              </div>
              <motion.div
                key={value}
                initial={{ scale: value === 0 ? 1 : 1.25 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="text-base font-bold tabular-nums leading-none mt-0.5"
              >
                {value}
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/** Capture éclair : mic with pulse rings → typed text → task chip. */
function CaptureVisual() {
  const { t } = useTranslation();
  // 4-phase loop driven by a single state machine:
  //   "idle"   → short pause before typing starts (mic pulses solo)
  //   "typing" → caret + characters appear one by one
  //   "done"   → fully typed sentence with checkmark + label chip
  //   "reset"  → brief blank before the next loop
  const sentence = t("landing_anim.capture_sentence");
  const TYPE_MS = 70; // per character
  const HOLD_MS = 1800;
  const RESET_MS = 500;

  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState<"idle" | "typing" | "done">("idle");

  useEffect(() => {
    let typeTimer: ReturnType<typeof setInterval> | null = null;
    let phaseTimer: ReturnType<typeof setTimeout> | null = null;

    const startTyping = () => {
      setPhase("typing");
      let i = 0;
      typeTimer = setInterval(() => {
        i += 1;
        setTyped(sentence.slice(0, i));
        if (i >= sentence.length) {
          if (typeTimer) clearInterval(typeTimer);
          setPhase("done");
          phaseTimer = setTimeout(() => {
            setTyped("");
            setPhase("idle");
            phaseTimer = setTimeout(startTyping, RESET_MS);
          }, HOLD_MS);
        }
      }, TYPE_MS);
    };

    phaseTimer = setTimeout(startTyping, 600);
    return () => {
      if (typeTimer) clearInterval(typeTimer);
      if (phaseTimer) clearTimeout(phaseTimer);
    };
  }, []);

  const isDone = phase === "done";
  const isTyping = phase === "typing";

  return (
    <div className="absolute inset-0 p-4 flex flex-col items-center justify-center gap-3">
      {/* Pulsing mic */}
      <div className="relative">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0.8, opacity: 0.6 }}
            animate={{ scale: 2.2, opacity: 0 }}
            transition={{
              duration: 1.8,
              delay: i * 0.6,
              repeat: Infinity,
              ease: "easeOut",
            }}
            className="absolute inset-0 rounded-full bg-primary/40"
          />
        ))}
        <div className="relative w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/40">
          <Mic className="w-4 h-4" />
        </div>
      </div>
      {/* Chip — width grows naturally with typed content */}
      <motion.div
        layout
        transition={{ layout: { duration: 0.2 } }}
        className="flex items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs shadow-sm min-h-[1.75rem]"
      >
        {/* Icon morphs from a small dot (typing) to a checkmark (done) */}
        {isDone ? (
          <motion.span
            key="check"
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 350, damping: 18 }}
            className="inline-flex"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
          </motion.span>
        ) : (
          <span className="w-3.5 h-3.5 inline-flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
          </span>
        )}
        <span className="font-medium tabular-nums">
          {typed || (phase === "idle" ? "\u00A0" : "")}
          {isTyping && (
            <motion.span
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.7, repeat: Infinity }}
              className="inline-block w-[1px] h-3 align-middle bg-foreground/70 ml-0.5"
            />
          )}
        </span>
        {isDone && (
          <motion.span
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="text-[10px] text-muted-foreground bg-muted rounded-full px-1.5"
          >
            {t("landing_anim.capture_label")}
          </motion.span>
        )}
      </motion.div>
    </div>
  );
}

/** Projets : project header with sub-rows that "light up" sequentially. */
function ProjectsVisual() {
  const { t } = useTranslation();
  const rows = [
    { Icon: CheckCircle2, label: t("landing_anim.project_row1"), color: "text-primary" },
    { Icon: FileText, label: t("landing_anim.project_row2"), color: "text-sky-500" },
    { Icon: Calendar, label: t("landing_anim.project_row3"), color: "text-amber-500" },
    { Icon: Mail, label: t("landing_anim.project_row4"), color: "text-fuchsia-500" },
  ];

  // Sweeping highlight: -1 = nothing active, then 0..3 light up in
  // turn, brief pause, full reset, repeat. Each row stays "activated"
  // (colored + subtle highlight) once visited until the cycle resets.
  const STEP_MS = 700;
  const RESET_PAUSE_MS = 1400;
  const [active, setActive] = useState(-1);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = (i: number) => {
      setActive(i);
      if (i < rows.length - 1) {
        timer = setTimeout(() => tick(i + 1), STEP_MS);
      } else {
        timer = setTimeout(() => {
          setActive(-1);
          timer = setTimeout(() => tick(0), 200);
        }, RESET_PAUSE_MS);
      }
    };
    timer = setTimeout(() => tick(0), 400);
    return () => clearTimeout(timer);
  }, [rows.length]);

  return (
    <div className="absolute inset-0 p-3 flex flex-col gap-1.5">
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-primary/10 text-primary text-xs font-semibold"
      >
        <FolderKanban className="w-3.5 h-3.5" />
        {t("landing_anim.project_header")}
      </motion.div>
      {rows.map((r, i) => {
        const isActivated = i <= active;
        const isCursor = i === active;
        return (
          <motion.div
            key={r.label}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
            className={`relative flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs transition-colors duration-300 ${
              isCursor
                ? "bg-primary/5 border-primary/30 shadow-sm"
                : isActivated
                  ? "bg-background border-border"
                  : "bg-muted/30 border-transparent"
            }`}
          >
            <r.Icon
              className={`w-3.5 h-3.5 transition-colors duration-300 ${
                isActivated ? r.color : "text-muted-foreground/40"
              }`}
            />
            <span
              className={`truncate flex-1 transition-colors duration-300 ${
                isActivated ? "text-foreground" : "text-muted-foreground/60"
              }`}
            >
              {r.label}
            </span>
            {isCursor && (
              <motion.span
                layoutId="projects-cursor"
                className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-primary"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

/** Assistant : chat bubble with typing dots that morphs into a streamed reply. */
function AssistantVisual() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"typing" | "answer">("typing");
  useEffect(() => {
    const a = setTimeout(() => setPhase("answer"), 1200);
    const b = setInterval(() => {
      setPhase("typing");
      setTimeout(() => setPhase("answer"), 1200);
    }, 4500);
    return () => {
      clearTimeout(a);
      clearInterval(b);
    };
  }, []);

  return (
    <div className="absolute inset-0 p-4 flex flex-col gap-2 justify-center">
      {/* User bubble */}
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
        className="self-end max-w-[80%] bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-3 py-1.5 text-xs"
      >
        {t("landing_anim.chat_user")}
      </motion.div>
      {/* Assistant bubble */}
      <div className="self-start max-w-[90%] flex items-end gap-1.5">
        <div className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Bot className="w-3.5 h-3.5" />
        </div>
        <motion.div
          layout
          className="bg-muted rounded-2xl rounded-tl-sm px-3 py-2 text-xs min-h-[2rem] flex items-center"
        >
          {phase === "typing" ? (
            <div className="flex items-center gap-1">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  animate={{ y: [0, -3, 0] }}
                  transition={{
                    duration: 0.8,
                    delay: i * 0.15,
                    repeat: Infinity,
                  }}
                  className="w-1.5 h-1.5 rounded-full bg-foreground/50"
                />
              ))}
            </div>
          ) : (
            <motion.span
              key="reply"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              {t("landing_anim.chat_ai")}
            </motion.span>
          )}
        </motion.div>
      </div>
    </div>
  );
}

/** Sécurité : a lock with pulsing rings + RGPD/AES badges. */
function SecurityVisual() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0.8, opacity: 0.5 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{
              duration: 2.4,
              delay: i * 0.8,
              repeat: Infinity,
              ease: "easeOut",
            }}
            className="absolute inset-0 rounded-2xl bg-primary/30"
          />
        ))}
        <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center shadow-xl shadow-primary/30">
          <Lock className="w-7 h-7" />
        </div>
      </div>
      {/* Floating badges */}
      <motion.div
        initial={{ opacity: 0, x: -20, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="absolute top-6 left-6 text-[10px] font-semibold bg-background border rounded-full px-2 py-0.5 shadow-sm"
      >
        AES-256
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 20, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="absolute bottom-6 right-6 text-[10px] font-semibold bg-background border rounded-full px-2 py-0.5 shadow-sm"
      >
        RGPD · FR
      </motion.div>
    </div>
  );
}

// ─── How it works ─────────────────────────────────────────────────────

interface Step {
  title: string;
  desc: string;
  Visual: () => ReactNode;
}

function HowItWorks() {
  const { t } = useTranslation();

  const STEPS: Step[] = [
    {
      title: t("how.s1_title"),
      desc: t("how.s1_desc"),
      Visual: ConnectStepVisual,
    },
    {
      title: t("how.s2_title"),
      desc: t("how.s2_desc"),
      Visual: SortStepVisual,
    },
    {
      title: t("how.s3_title"),
      desc: t("how.s3_desc"),
      Visual: AdvanceStepVisual,
    },
  ];

  return (
    <section
      id="how"
      className="relative py-24 px-6 bg-gradient-to-b from-background via-primary/5 to-background"
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeader
          eyebrow={t("how.eyebrow")}
          title={t("how.title")}
          subtitle={t("how.subtitle")}
        />

        <div className="mt-14 grid md:grid-cols-3 gap-6 relative">
          <div
            aria-hidden
            className="hidden md:block absolute top-24 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
          />
          {STEPS.map((s, i) => (
            <StepCard key={s.title} step={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index }: { step: Step; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { Visual } = step;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12, ease: "easeOut" }}
      className="relative text-center"
    >
      {/* Animated visual */}
      <div className="relative h-40 w-full rounded-2xl bg-card shadow-sm overflow-hidden mb-5">
        {inView && <Visual />}
        <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-background border-2 border-primary text-primary text-xs font-bold flex items-center justify-center shadow-sm">
          {index + 1}
        </div>
      </div>
      <h3 className="text-lg font-semibold tracking-tight">{step.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
        {step.desc}
      </p>
    </motion.div>
  );
}

// ─── Step visuals ──────────────────────────────────────────────────────

/** Connect : a mailbox icon emitting wifi-like waves toward an OneNext badge. */
function ConnectStepVisual() {
  return (
    <div className="absolute inset-0 flex items-center justify-around px-6">
      {/* Mailbox */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-12 h-12 rounded-xl bg-muted flex items-center justify-center"
      >
        <Mail className="w-5 h-5 text-foreground/70" />
      </motion.div>
      {/* Animated connection waves */}
      <div className="relative flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{ opacity: [0.2, 1, 0.2] }}
            transition={{
              duration: 1.4,
              delay: i * 0.2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="w-2 h-2 rounded-full bg-primary"
          />
        ))}
        <Wifi className="w-4 h-4 text-primary/60 ml-1" />
      </div>
      {/* OneNext badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="relative w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30 font-bold"
      >
        O
        <motion.div
          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 rounded-xl bg-primary/40 -z-10"
        />
      </motion.div>
    </div>
  );
}

/** Sort : neural-network brain — signals flow in from left, get sorted, exit right. */
function SortStepVisual() {
  // Centre cluster
  const nodes: { id: string; cx: number; cy: number }[] = [
    { id: "n0", cx: 95, cy: 28 },
    { id: "n1", cx: 72, cy: 52 },
    { id: "n2", cx: 118, cy: 52 },
    { id: "n3", cx: 80, cy: 80 },
    { id: "n4", cx: 111, cy: 80 },
    { id: "n5", cx: 95, cy: 100 },
  ];
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));

  const edges: [string, string][] = [
    ["n0", "n1"], ["n0", "n2"],
    ["n1", "n2"], ["n1", "n3"],
    ["n2", "n4"], ["n3", "n4"],
    ["n3", "n5"], ["n4", "n5"],
    ["n1", "n4"], ["n2", "n3"],
  ];

  // Left inputs  →  centre entry nodes
  const inputs = [
    { cx: 22, cy: 34, target: "n0" },
    { cx: 22, cy: 64, target: "n1" },
    { cx: 22, cy: 94, target: "n5" },
  ];

  // Centre exit nodes  →  right outputs
  const COLORS = [
    "hsl(217 91% 55%)",  // blue – prioritaire
    "hsl(38 90% 52%)",   // orange – normal
    "hsl(142 60% 45%)",  // green – archiver
  ];
  const outputs = [
    { cx: 172, cy: 34, source: "n0", color: COLORS[0] },
    { cx: 172, cy: 64, source: "n2", color: COLORS[1] },
    { cx: 172, cy: 94, source: "n5", color: COLORS[2] },
  ];

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <svg viewBox="0 0 200 128" className="w-full h-full" overflow="visible">
        {/* ── input lines ──────────────────────────────────── */}
        {inputs.map((inp, i) => {
          const t = nodeMap[inp.target];
          return (
            <motion.line
              key={`in-${i}`}
              x1={inp.cx} y1={inp.cy} x2={t.cx} y2={t.cy}
              stroke="hsl(var(--border))"
              strokeWidth={0.9}
              strokeDasharray="3 4"
              initial={{ strokeDashoffset: 14 }}
              animate={{ strokeDashoffset: [14, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.4, ease: "linear" }}
            />
          );
        })}

        {/* ── output lines ─────────────────────────────────── */}
        {outputs.map((out, i) => {
          const s = nodeMap[out.source];
          return (
            <motion.line
              key={`out-${i}`}
              x1={s.cx} y1={s.cy} x2={out.cx} y2={out.cy}
              stroke={out.color}
              strokeWidth={1}
              strokeDasharray="3 4"
              strokeOpacity={0.7}
              initial={{ strokeDashoffset: 0 }}
              animate={{ strokeDashoffset: [0, -14] }}
              transition={{ duration: 1.1, repeat: Infinity, delay: 1 + i * 0.35, ease: "linear" }}
            />
          );
        })}

        {/* ── inner network edges ───────────────────────────── */}
        {edges.map(([a, b], i) => {
          const na = nodeMap[a], nb = nodeMap[b];
          const len = Math.hypot(nb.cx - na.cx, nb.cy - na.cy);
          return (
            <motion.line
              key={`e-${i}`}
              x1={na.cx} y1={na.cy} x2={nb.cx} y2={nb.cy}
              stroke="hsl(var(--primary))"
              strokeWidth={0.7}
              strokeOpacity={0.3}
              strokeDasharray={`2 ${len}`}
              initial={{ strokeDashoffset: len }}
              animate={{ strokeDashoffset: [len, -len] }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                delay: i * 0.22,
                ease: "linear",
              }}
            />
          );
        })}

        {/* ── input circles ─────────────────────────────────── */}
        {inputs.map((inp, i) => (
          <motion.circle
            key={`ic-${i}`}
            cx={inp.cx} cy={inp.cy} r={5}
            fill="hsl(var(--muted))"
            stroke="hsl(var(--border))"
            strokeWidth={1}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.3 }}
          />
        ))}

        {/* ── centre neurons ────────────────────────────────── */}
        {nodes.map((n, i) => (
          <g key={n.id}>
            {/* glow ring */}
            <motion.circle
              cx={n.cx} cy={n.cy} r={7}
              fill="hsl(var(--primary) / 0.08)"
              animate={{ r: [5, 9, 5], opacity: [0.4, 0.1, 0.4] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.4 }}
            />
            {/* core */}
            <motion.circle
              cx={n.cx} cy={n.cy} r={4}
              fill="hsl(var(--primary) / 0.18)"
              stroke="hsl(var(--primary) / 0.7)"
              strokeWidth={1}
              animate={{ scale: [1, 1.25, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.4 }}
            />
          </g>
        ))}

        {/* ── output circles (coloured) ─────────────────────── */}
        {outputs.map((out, i) => (
          <g key={`oc-${i}`}>
            <motion.circle
              cx={out.cx} cy={out.cy} r={8}
              fill={out.color}
              fillOpacity={0.12}
              animate={{ r: [6, 10, 6], opacity: [0.5, 0.15, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1.2 + i * 0.4 }}
            />
            <motion.circle
              cx={out.cx} cy={out.cy} r={5}
              fill={out.color}
              fillOpacity={0.9}
              stroke={out.color}
              strokeWidth={1}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1.2 + i * 0.4 }}
            />
          </g>
        ))}

        {/* ── travelling signal dots ────────────────────────── */}
        {[
          { x1: 22, y1: 34, x2: 95, y2: 28, delay: 0, color: COLORS[0] },
          { x1: 22, y1: 64, x2: 72, y2: 52, delay: 0.5, color: COLORS[1] },
          { x1: 22, y1: 94, x2: 95, y2: 100, delay: 1, color: COLORS[2] },
          { x1: 95, y1: 28, x2: 172, y2: 34, delay: 1.4, color: COLORS[0] },
          { x1: 118, y1: 52, x2: 172, y2: 64, delay: 1.8, color: COLORS[1] },
          { x1: 95, y1: 100, x2: 172, y2: 94, delay: 2.2, color: COLORS[2] },
        ].map((sig, i) => (
          <motion.circle
            key={`sig-${i}`}
            r={2.2}
            fill={sig.color}
            initial={{ opacity: 0 }}
            animate={{
              cx: [sig.x1, sig.x2],
              cy: [sig.y1, sig.y2],
              opacity: [0, 1, 1, 0],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              repeatDelay: 2.2,
              delay: sig.delay,
              ease: "easeInOut",
              times: [0, 0.1, 0.9, 1],
            }}
          />
        ))}
      </svg>
    </div>
  );
}

/** Advance : checklist items tick off and the progress bar fills. */
function AdvanceStepVisual() {
  const { t } = useTranslation();
  const [done, setDone] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setDone((d) => (d >= 4 ? 0 : d + 1));
    }, 700);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="absolute inset-0 p-4 flex flex-col justify-center gap-2">
      {/* Mini check rows */}
      <div className="space-y-1.5">
        {[0, 1, 2, 3].map((i) => {
          const checked = i < done;
          return (
            <div key={i} className="flex items-center gap-2">
              <motion.div
                animate={{
                  backgroundColor: checked
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted))",
                  borderColor: checked
                    ? "hsl(var(--primary))"
                    : "hsl(var(--border))",
                }}
                className="w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center shrink-0"
              >
                {checked && (
                  <motion.svg
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.25 }}
                    className="w-2 h-2 text-primary-foreground"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={4}
                    strokeLinecap="round"
                  >
                    <motion.path d="M5 12l5 5L20 7" />
                  </motion.svg>
                )}
              </motion.div>
              <div
                className={`h-2 rounded-full flex-1 ${
                  checked ? "bg-primary/20" : "bg-muted"
                }`}
                style={{ maxWidth: `${60 + i * 8}%` }}
              />
            </div>
          );
        })}
      </div>
      {/* Progress bar */}
      <div className="mt-2">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
          <span>{t("app_nav.today")}</span>
          <span className="tabular-nums">{done} / 4</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <motion.div
            animate={{ width: `${(done / 4) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="h-full bg-primary"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────

const ANNUAL_FACTOR = 10 / 12;
function annualMonthlyPrice(monthly: number): number {
  if (monthly === 0) return 0;
  return Math.round(monthly * ANNUAL_FACTOR * 100) / 100;
}

function PricingSection() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");

  const planLabels: Record<string, { label: string; tagline: string; cta: string; features: string[] }> = {
    free: {
      label: t("plans.free_label"),
      tagline: t("plans.free_tagline"),
      cta: t("plans.free_cta"),
      features: [t("plans.free_f1"), t("plans.free_f2"), t("plans.free_f3"), t("plans.free_f4"), t("plans.free_f5")],
    },
    pro: {
      label: t("plans.pro_label"),
      tagline: t("plans.pro_tagline"),
      cta: t("plans.pro_cta"),
      features: [t("plans.pro_f1"), t("plans.pro_f2"), t("plans.pro_f3"), t("plans.pro_f4"), t("plans.pro_f5")],
    },
    unlimited: {
      label: t("plans.unlimited_label"),
      tagline: t("plans.unlimited_tagline"),
      cta: t("plans.unlimited_cta"),
      features: [t("plans.unlimited_f1"), t("plans.unlimited_f2"), t("plans.unlimited_f3"), t("plans.unlimited_f4"), t("plans.unlimited_f5")],
    },
  };

  return (
    <section
      id="pricing"
      className="py-24 px-6 bg-gradient-to-b from-background via-primary/5 to-background"
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeader
          eyebrow={t("pricing_section.eyebrow")}
          title={t("pricing_section.title")}
          subtitle={t("pricing_section.subtitle")}
        />

        {/* Billing toggle */}
        <div className="mt-8 flex flex-col items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-full border bg-muted/40 p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                billing === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {i18n.language === "en" ? "Monthly" : "Mensuel"}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billing === "annual"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {i18n.language === "en" ? "Annual" : "Annuel"}
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded-full px-1.5 py-0.5">
                <Zap className="w-2.5 h-2.5" />–17%
              </span>
            </button>
          </div>
          {billing === "annual" && (
            <p className="text-xs text-muted-foreground">
              {i18n.language === "en" ? "Billed annually — 2 months free" : "Facturé en une fois — 2 mois offerts"}
            </p>
          )}
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {PUBLIC_PLANS.map((p) => {
            const translated = planLabels[p.id] ?? { label: p.label, tagline: p.tagline, cta: p.cta, features: p.features };
            return (
              <div
                key={p.id}
                data-testid={`landing-pricing-${p.id}`}
                className={`relative flex flex-col rounded-2xl border bg-card p-6 ${
                  p.highlight
                    ? "border-primary shadow-xl shadow-primary/10 ring-1 ring-primary/20"
                    : ""
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 shadow-sm">
                    <Sparkles className="w-3 h-3" />
                    {t("pricing_section.recommended")}
                  </div>
                )}
                <h3 className="text-xl font-semibold tracking-tight">{translated.label}</h3>
                <p className="mt-1 text-sm text-muted-foreground min-h-[2.5rem]">
                  {translated.tagline}
                </p>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-4xl font-bold tracking-tight">
                    Gratuit
                  </span>
                  {p.monthlyPriceEur > 0 && (
                    <span className="text-lg text-muted-foreground line-through tabular-nums">
                      {p.monthlyPriceEur}€
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs font-medium text-primary">
                  Gratuit pendant la beta — jusqu'au 30 septembre 2026
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {p.monthlyCredits === null
                    ? t("pricing_section.unlimited_credits")
                    : t("pricing_section.credits_per_month", { count: p.monthlyCredits.toLocaleString(locale) })}
                </p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {translated.features.slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link href="/sign-up" className="mt-6 block">
                  <Button
                    size="lg"
                    variant={p.highlight ? "default" : "outline"}
                    className="w-full"
                  >
                    {translated.cta}
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <Link
            href="/pricing"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            {t("pricing_section.compare_link")}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────

function FaqSection() {
  const { t } = useTranslation();

  const TRANSLATED_FAQ = [
    { q: t("faq_items.q1"), a: t("faq_items.a1") },
    { q: t("faq_items.q2"), a: t("faq_items.a2") },
    { q: t("faq_items.q3"), a: t("faq_items.a3") },
    { q: t("faq_items.q4"), a: t("faq_items.a4") },
    { q: t("faq_items.q5"), a: t("faq_items.a5") },
    { q: t("faq_items.q6"), a: t("faq_items.a6") },
    { q: t("faq_items.q7"), a: t("faq_items.a7") },
    { q: t("faq_items.q8"), a: t("faq_items.a8") },
    { q: t("faq_items.q9"), a: t("faq_items.a9") },
  ];

  return (
    <section id="faq" className="py-24 px-6">
      <div className="mx-auto max-w-3xl">
        <SectionHeader
          eyebrow={t("faq_section.eyebrow")}
          title={t("faq_section.title")}
          subtitle={t("faq_section.subtitle")}
        />
        <Accordion
          type="single"
          collapsible
          className="mt-10 rounded-2xl border bg-card divide-y"
          data-testid="landing-faq"
        >
          {TRANSLATED_FAQ.map((f, i) => (
            <AccordionItem
              key={i}
              value={`landing-faq-${i}`}
              className="border-0 px-5"
            >
              <AccordionTrigger className="text-base font-medium">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t("faq_section.more_questions")}{" "}
          <Link href="/contact" className="text-primary hover:underline">
            {t("faq_section.contact_us")}
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

// ─── Testimonial ──────────────────────────────────────────────────────

function Testimonial() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="testimonial" className="py-24 px-6">
      <div className="mx-auto max-w-3xl text-center">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <TestimonialContent />
        </motion.div>
      </div>
    </section>
  );
}

function TestimonialContent() {
  const { t } = useTranslation();
  return (
    <>
      <div className="text-primary text-5xl leading-none">"</div>
      <p className="mt-2 text-2xl md:text-3xl font-medium tracking-tight leading-snug">
        {t("testimonial.quote_before")}{" "}
        <span className="text-primary">{t("testimonial.quote_highlight")}</span>{" "}
        {t("testimonial.quote_after")}
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 text-primary-foreground flex items-center justify-center font-semibold">
          CL
        </div>
        <div className="text-left">
          <p className="text-sm font-semibold">{t("testimonial.author")}</p>
          <p className="text-xs text-muted-foreground">
            {t("testimonial.role")}
          </p>
        </div>
      </div>
    </>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────

function FinalCTA() {
  const { t } = useTranslation();
  return (
    <section className="px-6 pb-24">
      <div className="mx-auto max-w-5xl">
        <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-primary to-primary/70 text-primary-foreground p-10 md:p-16 text-center">
          <motion.div
            aria-hidden
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="absolute -top-1/2 -right-1/4 w-[600px] h-[600px] rounded-full bg-white/10 blur-3xl"
          />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("cta.title")}
            </h2>
            <p className="mt-4 text-base md:text-lg text-primary-foreground/85 max-w-2xl mx-auto">
              {t("cta.subtitle")}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <Link href="/sign-up">
                <Button
                  size="lg"
                  variant="secondary"
                  className="h-12 px-7 text-base bg-white text-primary hover:bg-white/90"
                >
                  {t("cta.start")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href="/sign-in">
                <Button
                  size="lg"
                  variant="ghost"
                  className="h-12 px-6 text-base text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                >
                  {t("cta.already_account")}
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-primary-foreground/75 inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {t("cta.setup_time")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Shared section header ────────────────────────────────────────────

function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: ReactNode;
  subtitle: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className="text-center max-w-2xl mx-auto"
    >
      <p className="text-xs uppercase tracking-widest text-primary font-semibold">
        {eyebrow}
      </p>
      <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
        {title}
      </h2>
      <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
        {subtitle}
      </p>
    </motion.div>
  );
}
